// Tamable archetype (MOB-2 tamable: wolf/cat, M13.1). PURE (ENG-1).
//
// Three states drive a tamable animal:
//   • sitting  → hold position (ordered to stay);
//   • tamed    → follow the owner, catching up past FOLLOW_MAX and teleporting
//                back if it falls TELEPORT_DIST behind (a pet never gets lost);
//   • wild     → behave like a grazer (wander, flee when hurt).
// Per-name overrides (wolf) layer neutral anger + owner-assist targeting on top.
import { TAME } from '../../config';
import type { MobDef } from '../../registry/schemas';
import type { Entity, MobContext } from '../entity';
import { grazer } from './grazer';
import { wander, moveToward, moveAway, tickSpeed } from './shared';

/** Follow the owner (nearest player, creative included): catch up when far, hold
 *  a small gap when close, and re-home by teleport when hopelessly behind. */
export function followOwner(e: Entity, def: MobDef, ctx: MobContext): void {
  const owner = ctx.sys.nearestAnyPlayer(ctx.frame, e.x, e.y, e.z, TAME.TELEPORT_DIST * 4);
  if (!owner) { wander(e, ctx, def); return; }
  const pl = ctx.frame.players[owner.idx]!;
  if (owner.dist > TAME.TELEPORT_DIST) {
    // Re-home next to the owner (physics settles it onto the ground).
    e.x = pl.x; e.y = pl.y; e.z = pl.z;
    e.vx = 0; e.vy = 0; e.vz = 0;
    return;
  }
  if (owner.dist > TAME.FOLLOW_MAX) moveToward(e, ctx, pl.x, pl.z, tickSpeed(def, 1.3));
  else if (owner.dist < TAME.FOLLOW_MIN) moveAway(e, ctx, pl.x, pl.z, tickSpeed(def, 0.6));
  else wander(e, ctx, def); // in the comfort band: idle near the owner
}

export function tamable(e: Entity, def: MobDef, ctx: MobContext): void {
  if (e.sitting) return; // stay put where ordered
  if (e.tamed) { followOwner(e, def, ctx); return; }
  grazer(e, def, ctx); // wild: wander + flee when hurt (+ breed if it has a food)
}
